import React, { useState, useEffect } from 'react';
import { Instagram, Volume2, VolumeX, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../utils/api';

// ── YouTube helpers ──────────────────────────────────────────────────────────
function extractYouTubeId(url) {
  if (!url) return null;
  const short = url.match(/youtu\.be\/([^?&]+)/);
  if (short) return short[1];
  const long = url.match(/(?:v=|\/embed\/|\/shorts\/)([^?&/]+)/);
  if (long) return long[1];
  if (/^[a-zA-Z0-9_-]{11}$/.test(url)) return url;
  return null;
}

function buildEmbedUrl(videoId, muted = true) {
  if (!videoId) return '';
  const params = new URLSearchParams({
    autoplay: '1',
    mute: muted ? '1' : '0',
    loop: '1',
    playlist: videoId,      // required for loop to work
    controls: '0',          // hide controls — clean reel look
    modestbranding: '1',
    rel: '0',
    playsinline: '1',
    vq: 'hd1080',           // request highest quality
  });
  return `https://www.youtube-nocookie.com/embed/${videoId}?${params}`;
}
// ─────────────────────────────────────────────────────────────────────────────

const ReelsSection = () => {
  const [reels, setReels] = useState([]);
  const [instagramUrl, setInstagramUrl] = useState('https://www.instagram.com/ahmedabadnx2014/');
  const [activeIdx, setActiveIdx] = useState(0);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    fetchReels();
    fetchInstagram();
  }, []);

  const fetchReels = async () => {
    try {
      const r = await api.get('/reels');
      setReels(r.data);
    } catch (e) { console.error(e); }
  };

  const fetchInstagram = async () => {
    try {
      const r = await api.get('/settings/instagram_handle');
      if (r.data.value) setInstagramUrl(r.data.value);
    } catch (e) { console.error(e); }
  };

  if (reels.length === 0) return null;

  const visibleReels = reels.slice(0, 4);
  const activeReel   = visibleReels[activeIdx];
  const videoId      = extractYouTubeId(activeReel?.instagram_url);

  const prev = () => setActiveIdx(i => (i - 1 + visibleReels.length) % visibleReels.length);
  const next = () => setActiveIdx(i => (i + 1) % visibleReels.length);

  return (
    <section className="py-16 md:py-24 bg-[#FFFDF9]" data-testid="reels-section">
      <div className="container mx-auto px-4 md:px-8">

        {/* Heading */}
        <div className="text-center mb-10 md:mb-14">
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-[#2A0000] mb-2">
            Reels & Stories
          </h2>
          <p className="text-[#2C2C2C] text-sm md:text-base">Follow our journey on Instagram</p>
        </div>

        {/* ── Layout: active player + thumbnail strip ── */}
        <div className="flex flex-col lg:flex-row items-center lg:items-start justify-center gap-6 lg:gap-8">

          {/* ── Active video player ── */}
          <div className="relative flex-shrink-0" style={{ width: 'min(320px, 85vw)' }}>
            <div
              className="relative w-full overflow-hidden rounded-2xl shadow-2xl bg-black"
              style={{ aspectRatio: '9/16' }}
            >
              {videoId ? (
                <iframe
                  key={`${videoId}-${muted}`}
                  src={buildEmbedUrl(videoId, muted)}
                  title={activeReel?.caption || 'Reel'}
                  allow="autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full border-0"
                  style={{ pointerEvents: 'none' }}
                />
              ) : activeReel?.thumbnail ? (
                <img
                  src={activeReel.thumbnail}
                  alt={activeReel?.caption || 'Reel'}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : null}

              {/* Mute / unmute button */}
              <button
                onClick={() => setMuted(m => !m)}
                className="absolute top-3 right-3 z-20 w-9 h-9 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                aria-label={muted ? 'Unmute' : 'Mute'}
              >
                {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              </button>

              {/* Caption overlay */}
              {activeReel?.caption && (
                <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 via-black/30 to-transparent pointer-events-none z-10">
                  <p className="text-white text-sm font-medium leading-snug">{activeReel.caption}</p>
                </div>
              )}

              {/* Prev / Next arrows */}
              {visibleReels.length > 1 && (
                <>
                  <button
                    onClick={prev}
                    className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/60 transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={next}
                    className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/60 transition-colors"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}
            </div>

            {/* Dot indicators */}
            {visibleReels.length > 1 && (
              <div className="flex justify-center gap-1.5 mt-3">
                {visibleReels.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveIdx(i)}
                    className={`rounded-full transition-all duration-300 ${
                      i === activeIdx
                        ? 'w-5 h-2 bg-[#E5A93C]'
                        : 'w-2 h-2 bg-gray-300 hover:bg-gray-400'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>

          {/* ── Thumbnail strip ── */}
          {visibleReels.length > 1 && (
            <div className="flex flex-row lg:flex-col gap-3 overflow-x-auto lg:overflow-visible pb-1 lg:pb-0 max-w-full">
              {visibleReels.map((reel, idx) => {
                const rid   = extractYouTubeId(reel.instagram_url);
                const thumb = reel.thumbnail || (rid ? `https://img.youtube.com/vi/${rid}/mqdefault.jpg` : '');
                const isActive = idx === activeIdx;
                return (
                  <button
                    key={idx}
                    onClick={() => setActiveIdx(idx)}
                    data-testid={`reel-${idx}`}
                    className={`relative flex-shrink-0 overflow-hidden rounded-xl transition-all duration-200 focus:outline-none ${
                      isActive
                        ? 'ring-2 ring-[#E5A93C] ring-offset-2 scale-[1.04] opacity-100'
                        : 'opacity-60 hover:opacity-90 hover:scale-[1.02]'
                    }`}
                    style={{ width: 72, aspectRatio: '9/16' }}
                  >
                    {thumb
                      ? <img src={thumb} alt={reel.caption || `Reel ${idx + 1}`} className="w-full h-full object-cover" />
                      : <div className="w-full h-full bg-gray-300" />
                    }
                    <div className={`absolute inset-0 flex items-center justify-center ${isActive ? 'bg-black/5' : 'bg-black/25'}`}>
                      <svg className="w-5 h-5 text-white fill-white drop-shadow" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Instagram CTA ── */}
        <div className="text-center mt-10 md:mt-14">
          <a
            href={instagramUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center space-x-2 btn-primary"
            data-testid="more-reels-button"
          >
            <Instagram className="w-5 h-5" />
            <span>More Reels on Instagram</span>
          </a>
        </div>

      </div>
    </section>
  );
};

export default ReelsSection;